import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  IconButton,
  Chip,
  Grid,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Stack,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
  Divider,
  Link,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  DeleteForever as DeleteForeverIcon,
  Refresh as RefreshIcon,
  ViewList as ViewListIcon,
  ViewModule as ViewModuleIcon,
  AddTask as AddTaskIcon,
  Download as DownloadIcon,
  InsertDriveFile as InsertDriveFileIcon,
  Email as EmailIcon,
} from "@mui/icons-material";
import MDBox from "../MDBox";
import MDTypography from "../MDTypography";
import MDButton from "../MDButton";
import TaskDialog from "./TaskDialog";

// API services
import { tasksAPI } from "../../services/api";

import EventIcon from "@mui/icons-material/Event";
import { Link as RouterLink, useLocation } from "react-router-dom";

const TodoList = () => {
  const location = useLocation();
  const currentUser = useSelector((state) => state.auth.user);
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editTask, setEditTask] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [isClearing, setIsClearing] = useState(false);
  const [processingTaskId, setProcessingTaskId] = useState(null);
  const [subtasks, setSubtasks] = useState({});
  const [selectedParentId, setSelectedParentId] = useState(null);
  const [viewMode, setViewMode] = useState("list"); // 'list' | 'board'
  const [expandedRows, setExpandedRows] = useState(new Set());

  const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const taskIdParam = params.get("task");
    if (taskIdParam) {
      const taskId = parseInt(taskIdParam, 10);
      if (!isNaN(taskId)) {
        setExpandedRows((prev) => {
          const newSet = new Set(prev);
          newSet.add(taskId);
          return newSet;
        });
        fetchSubtasks(taskId);

        // Optional: Scroll to task logic could be added here
        // or ensure the task is loaded if not already in the list.
      }
    }
  }, [location.search]);

  useEffect(() => {
    fetchTasks();
    fetchUsers();
  }, []);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 10000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(null), 10000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  const fetchTasks = async (force = false) => {
    const cachedTasksString = localStorage.getItem("tasks");
    const cachedTimestamp = localStorage.getItem("tasks_timestamp");
    let isCacheValid = false;

    if (!force && cachedTasksString) {
      try {
        const tasksData = JSON.parse(cachedTasksString);
        setTasks(tasksData);
        setLoading(false);

        if (
          cachedTimestamp &&
          Date.now() - parseInt(cachedTimestamp) < CACHE_DURATION
        ) {
          isCacheValid = true;
        }
      } catch (e) {
        console.error(
          "Fehler beim Parsen der zwischengespeicherten Aufgaben",
          e,
        );
      }
    }

    if (!force && isCacheValid) {
      return;
    }

    if (force) {
      setLoading(true);
    }

    try {
      const response = await tasksAPI.getTasks();
      if (response.data) {
        setTasks(response.data);
        localStorage.setItem("tasks", JSON.stringify(response.data));
        localStorage.setItem("tasks_timestamp", Date.now().toString());
      }
    } catch (err) {
      console.error("Fehler beim Laden der Aufgaben:", err);
      if (force) {
        setError("Fehler beim Aktualisieren der Aufgaben.");
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    const cachedUsersString = localStorage.getItem("users");
    const cachedTimestamp = localStorage.getItem("users_timestamp");
    let isCacheValid = false;

    if (cachedUsersString) {
      try {
        const usersData = JSON.parse(cachedUsersString);
        setUsers(usersData);

        if (
          cachedTimestamp &&
          Date.now() - parseInt(cachedTimestamp) < CACHE_DURATION
        ) {
          isCacheValid = true;
        }
      } catch (e) {
        console.error(
          "Fehler beim Parsen der zwischengespeicherten Benutzer",
          e,
        );
      }
    }

    if (isCacheValid) {
      return;
    }

    try {
      const response = await tasksAPI.getUsers();
      setUsers(response.data);
      localStorage.setItem("users", JSON.stringify(response.data));
      localStorage.setItem("users_timestamp", Date.now().toString());
    } catch (err) {
      console.error("Fehler beim Laden der Benutzer:", err);
    }
  };

  const fetchSubtasks = async (parentId) => {
    try {
      const response = await tasksAPI.getSubtasks(parentId);
      setSubtasks((prev) => ({
        ...prev,
        [parentId]: response.data,
      }));
    } catch (err) {
      console.error("Fehler beim Laden der Unteraufgaben:", err);
    }
  };

  const handleCreateTask = async (taskData, files) => {
    try {
      const response = await tasksAPI.createTask(taskData);

      if (files && files.length > 0) {
        try {
          const formData = new FormData();
          files.forEach((file) => formData.append("files", file));
          await tasksAPI.uploadAttachments(response.data.id, formData);
        } catch (uploadErr) {
          console.error("Fehler beim Hochladen der Anhänge", uploadErr);
          // Don't fail the task creation, just log error for now or show warning
        }
      }

      if (taskData.parent_task_id) {
        // It's a subtask, refresh subtasks for the parent
        fetchSubtasks(taskData.parent_task_id);
      } else {
        // Refresh the whole list so attachments and relations are consistent
        await fetchTasks(true);
      }

      handleCloseDialog();
    } catch (err) {
      console.error("Fehler beim Erstellen der Aufgabe:", err);
      setError(
        "Fehler beim Erstellen der Aufgabe. Bitte versuchen Sie es erneut.",
      );
    }
  };

  const handleUpdateTask = async (taskData, files) => {
    try {
      const response = await tasksAPI.updateTask(editTask.id, taskData);

      if (files && files.length > 0) {
        try {
          const formData = new FormData();
          files.forEach((file) => formData.append("files", file));
          await tasksAPI.uploadAttachments(editTask.id, formData);
        } catch (uploadErr) {
          console.error("Fehler beim Hochladen der Anhänge:", uploadErr);
        }
      }

      if (response.data.parent_task_id) {
        fetchSubtasks(response.data.parent_task_id);
      } else {
        // Refresh full list so attachments show up consistently
        await fetchTasks(true);
      }

      handleCloseDialog();
    } catch (err) {
      console.error("Fehler beim Aktualisieren der Aufgabe:", err);
      setError(
        "Fehler beim Aktualisieren der Aufgabe. Bitte versuchen Sie es erneut.",
      );
    }
  };

  const handleDeleteTask = async (taskId, parentId = null) => {
    try {
      await tasksAPI.deleteTask(taskId);
      if (parentId) {
        fetchSubtasks(parentId);
      } else {
        const filteredTasks = tasks.filter((task) => task.id !== taskId);
        setTasks(filteredTasks);
        localStorage.setItem("tasks", JSON.stringify(filteredTasks));
        localStorage.setItem("tasks_timestamp", Date.now().toString());
      }
    } catch (err) {
      console.error("Fehler beim Löschen der Aufgabe:", err);
      setError(
        "Fehler beim Löschen der Aufgabe. Bitte versuchen Sie es erneut.",
      );
    }
  };

  const handleClearArchivedTasks = async () => {
    if (
      !window.confirm(
        "Sind Sie sicher, dass Sie ALLE archivierten Aufgaben löschen möchten? Dies kann nicht rückgängig gemacht werden.",
      )
    ) {
      return;
    }
    setIsClearing(true);
    try {
      const response = await tasksAPI.clearArchivedTasks();
      await fetchTasks(true);
      setSuccessMessage(
        response.data.detail || "Archivierte Aufgaben erfolgreich gelöscht.",
      );
      setTimeout(() => setSuccessMessage(null), 5000);
    } catch (err) {
      console.error("Fehler beim Löschen aller archivierten Aufgaben:", err);
      setError(
        "Fehler beim Löschen aller archivierten Aufgaben. Bitte versuchen Sie es erneut.",
      );
    } finally {
      setIsClearing(false);
    }
  };

  const handleDeleteArchivedTask = async (taskId, parentId = null) => {
    if (
      !window.confirm(
        "Sind Sie sicher, dass Sie diese archivierte Aufgabe dauerhaft löschen möchten?",
      )
    ) {
      return;
    }
    setProcessingTaskId(taskId);
    try {
      const response = await tasksAPI.deleteArchivedTask(taskId);

      if (parentId) {
        fetchSubtasks(parentId);
      } else {
        const filteredTasks = tasks.filter((task) => task.id !== taskId);
        setTasks(filteredTasks);
        localStorage.setItem("tasks", JSON.stringify(filteredTasks));
        localStorage.setItem("tasks_timestamp", Date.now().toString());
      }

      setSuccessMessage(
        response.data.detail || "Aufgabe erfolgreich dauerhaft gelöscht.",
      );
      setTimeout(() => setSuccessMessage(null), 5000);
    } catch (err) {
      console.error("Fehler beim Löschen der archivierten Aufgabe:", err);
      setError(
        "Fehler beim Löschen der archivierten Aufgabe. Bitte versuchen Sie es erneut.",
      );
    } finally {
      setProcessingTaskId(null);
    }
  };

  const getPriorityLabel = (priority) => {
    switch (priority) {
      case "low":
        return "Low";
      case "medium":
        return "Medium";
      case "high":
        return "High";
      case "urgent":
        return "Urgent";
      default:
        return priority;
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case "low":
        return "info";
      case "medium":
        return "warning";
      case "high":
        return "error";
      case "urgent":
        return "error";
      default:
        return "default";
    }
  };

  const handleDownloadAttachment = async (taskId, attachment) => {
    try {
      const response = await tasksAPI.downloadAttachment(taskId, attachment.id);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", attachment.filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (e) {
      console.error("Failed to download attachment", e);
      setError("Fehler beim Herunterladen des Anhangs");
    }
  };

  const handleDeleteAttachment = async (taskId, attachmentId) => {
    if (!window.confirm("Anhang wirklich löschen?")) return;
    try {
      await tasksAPI.deleteAttachment(taskId, attachmentId);

      // Update local state
      setTasks((prevTasks) =>
        prevTasks.map((task) => {
          if (task.id === taskId) {
            return {
              ...task,
              attachments: task.attachments.filter(
                (a) => a.id !== attachmentId,
              ),
            };
          }
          return task;
        }),
      );
    } catch (e) {
      console.error("Failed to delete attachment", e);
      setError("Fehler beim Löschen des Anhangs");
    }
  };

  const handleOpenDialog = (task = null, parentTaskId = null) => {
    setEditTask(task);
    setSelectedParentId(parentTaskId);
    setOpen(true);
  };

  const handleCloseDialog = () => {
    setOpen(false);
    setEditTask(null);
    setSelectedParentId(null);
  };

  const handleSaveTask = (taskData, files) => {
    if (editTask) {
      handleUpdateTask(taskData, files);
    } else {
      handleCreateTask(taskData, files);
    }
  };

  const todoTasks = tasks.filter((t) => t.status === "todo");
  const inProgressTasks = tasks.filter((t) => t.status === "in_progress");
  const doneTasks = tasks.filter((t) => t.status === "done");

  const renderTaskCard = (task) => (
    <Card
      key={task.id}
      sx={{
        backgroundColor: task.is_archived ? "rgba(255, 255, 0, 0.1)" : "white",
        "&:hover": {
          boxShadow: 3,
        },
        position: "relative",
        mb: 2,
      }}
    >
      <CardContent sx={{ p: 2 }}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="flex-start"
          mb={1}
        >
          <Box>
            {task.is_archived && (
              <Chip
                label="Archived"
                size="small"
                color="warning"
                variant="outlined"
                sx={{ mb: 0.5 }}
              />
            )}
            <Typography
              variant="h6"
              component="div"
              sx={{ fontSize: "1rem", fontWeight: "bold" }}
            >
              {task.title}
            </Typography>
          </Box>
          <Chip
            label={getPriorityLabel(task.priority)}
            color={getPriorityColor(task.priority)}
            size="small"
            sx={{ height: "20px", fontSize: "0.7rem" }}
          />
        </Box>

        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mb: 2, minHeight: "20px" }}
        >
          {task.description.length > 150
            ? task.description.substring(0, 150) + "..."
            : task.description}
        </Typography>

        {task.deadline && (
          <Box display="flex" alignItems="center" gap={0.5} mb={1}>
            <EventIcon color="action" fontSize="small" />
            <Typography variant="caption" color="text.secondary">
              Deadline: {new Date(task.deadline).toLocaleDateString("de-DE")}
            </Typography>
          </Box>
        )}

        <Divider sx={{ my: 1 }} />

        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" flexDirection="column">
            <Typography variant="caption" color="text.secondary">
              Zugewiesen an:
            </Typography>
            <Typography variant="caption" fontWeight="medium">
              {task.assignees && task.assignees.length > 0
                ? task.assignees
                    .map((user) => user.full_name || user.username)
                    .join(", ")
                : "Nicht zugewiesen"}
            </Typography>
          </Box>
          <Typography variant="caption" color="text.secondary">
            {new Date(task.created_at).toLocaleDateString("de-DE")}
          </Typography>
        </Box>

        <Box display="flex" justifyContent="flex-end" mt={2} gap={1}>
          {task.is_archived && currentUser?.is_superuser && (
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteArchivedTask(task.id);
              }}
              color="error"
              title="Dauerhaft löschen"
              disabled={processingTaskId === task.id}
            >
              {processingTaskId === task.id ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <DeleteForeverIcon fontSize="small" />
              )}
            </IconButton>
          )}
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              handleOpenDialog(task);
            }}
            color="info"
          >
            <EditIcon fontSize="small" />
          </IconButton>
          {task.calendar_event_id && (
            <IconButton
              size="small"
              component={RouterLink}
              to={`/calendar?event=${task.calendar_event_id}`}
              onClick={(e) => e.stopPropagation()}
              color="primary"
              title="Zum Kalendereintrag"
            >
              <EventIcon fontSize="small" />
            </IconButton>
          )}
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              if (
                window.confirm(
                  "Sind Sie sicher, dass Sie diese Aufgabe löschen möchten?",
                )
              ) {
                handleDeleteTask(task.id);
              }
            }}
            color="error"
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      </CardContent>
    </Card>
  );

  const renderBoardColumn = (title, tasks, color) => (
    <Grid item xs={12} md={4}>
      <Paper elevation={0} sx={{ p: 2, height: "100%", bgcolor: "#f8f9fa" }}>
        <Box
          sx={{
            p: 2,
            mb: 2,
            bgcolor: `${color}.main`,
            color: "white",
            borderRadius: 1,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            boxShadow: 2,
          }}
        >
          <Typography variant="h6" color="white" fontWeight="medium">
            {title}
          </Typography>
          <Chip
            label={tasks.length}
            size="small"
            sx={{
              bgcolor: "white",
              color: `${color}.main`,
              fontWeight: "bold",
            }}
          />
        </Box>
        <Stack spacing={2}>
          {loading ? (
            <Box display="flex" justifyContent="center" p={2}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            tasks.map(renderTaskCard)
          )}
          {tasks.length === 0 && !loading && (
            <Box sx={{ p: 2, textAlign: "center", color: "text.disabled" }}>
              <Typography variant="body2">Keine Aufgaben</Typography>
            </Box>
          )}
        </Stack>
      </Paper>
    </Grid>
  );

  const renderTaskRow = (task) => (
    <React.Fragment key={task.id}>
      <TableRow
        sx={{
          cursor: "pointer",
          backgroundColor: task.is_archived
            ? "rgba(255, 255, 0, 0.1)"
            : "inherit",
          "&:hover": {
            backgroundColor: task.is_archived
              ? "rgba(255, 255, 0, 0.2)"
              : "rgba(0, 123, 255, 0.4)",
          },
          "&:last-child td, &:last-child th": { border: 0 },
        }}
        onClick={() => {
          const newExpanded = new Set(expandedRows);
          if (newExpanded.has(task.id)) {
            newExpanded.delete(task.id);
          } else {
            newExpanded.add(task.id);
            fetchSubtasks(task.id);
          }
          setExpandedRows(newExpanded);
        }}
      >
        <TableCell component="th" scope="row">
          <Box display="flex" alignItems="center" gap={1}>
            {task.is_archived && (
              <Chip
                label="Archived"
                size="small"
                color="warning"
                variant="outlined"
              />
            )}
            <Box>
              <Typography variant="subtitle2" fontWeight="medium">
                {task.title}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {task.description && task.description.length > 100
                  ? task.description.substring(0, 100) + "..."
                  : task.description}
              </Typography>
            </Box>
          </Box>
        </TableCell>
        <TableCell>
          <Chip
            label={getPriorityLabel(task.priority)}
            color={getPriorityColor(task.priority)}
            size="small"
          />
        </TableCell>
        <TableCell>
          {task.assignees && task.assignees.length > 0 ? (
            <Typography variant="body2">
              {task.assignees
                .map((user) => user.full_name || user.username)
                .join(", ")}
            </Typography>
          ) : (
            <Typography variant="caption" color="text.secondary">
              Nicht zugewiesen
            </Typography>
          )}
        </TableCell>
        <TableCell>
          <Box display="flex" flexDirection="column">
            <Typography variant="body2">
              {new Date(task.created_at).toLocaleDateString("de-DE")}
            </Typography>
            {task.deadline && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
              >
                <EventIcon fontSize="inherit" />
                {new Date(task.deadline).toLocaleDateString("de-DE")}
              </Typography>
            )}
          </Box>
        </TableCell>
        <TableCell align="right">
          {task.is_archived && currentUser?.is_superuser && (
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteArchivedTask(task.id);
              }}
              color="error"
              title="Dauerhaft löschen"
            >
              <DeleteForeverIcon />
            </IconButton>
          )}
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              handleOpenDialog(null, task.id);
            }}
            color="primary"
            title="Unteraufgabe erstellen"
          >
            <AddTaskIcon />
          </IconButton>
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              handleOpenDialog(task);
            }}
            color="info"
          >
            <EditIcon />
          </IconButton>
          {task.calendar_event_id && (
            <IconButton
              component={RouterLink}
              to={`/calendar?event=${task.calendar_event_id}`}
              onClick={(e) => e.stopPropagation()}
              color="primary"
              title="Zum Kalendereintrag"
            >
              <EventIcon />
            </IconButton>
          )}
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              if (
                window.confirm(
                  "Sind Sie sicher, dass Sie diese Aufgabe löschen möchten?",
                )
              ) {
                handleDeleteTask(task.id);
              }
            }}
            color="error"
          >
            <DeleteIcon />
          </IconButton>
        </TableCell>
      </TableRow>
      {expandedRows.has(task.id) && (
        <TableRow>
          <TableCell
            colSpan={5}
            sx={{
              backgroundColor: task.is_archived
                ? "rgba(255, 255, 0, 0.05)"
                : "rgba(0, 123, 255, 0.05)",
              padding: 2,
            }}
          >
            <Box mb={2} display="flex" gap={3}>
              {task.tenant && (
                <Box>
                  <Typography
                    variant="caption"
                    fontWeight="bold"
                    display="block"
                  >
                    Mieter:
                  </Typography>
                  <Link
                    component={RouterLink}
                    to={`/asset-overview?tab=tenants&search=${task.tenant.id}`}
                    onClick={(e) => e.stopPropagation()}
                  >
                    {task.tenant.name}
                  </Link>
                </Box>
              )}
              {task.property && (
                <Box>
                  <Typography
                    variant="caption"
                    fontWeight="bold"
                    display="block"
                  >
                    Immobilie:
                  </Typography>
                  <Link
                    component={RouterLink}
                    to={`/asset-overview?tab=properties&search=${task.property.id}`}
                    onClick={(e) => e.stopPropagation()}
                  >
                    {task.property.address}
                  </Link>
                </Box>
              )}
              {task.artisan && (
                <Box>
                  <Typography
                    variant="caption"
                    fontWeight="bold"
                    display="block"
                  >
                    Handwerker:
                  </Typography>
                  <Link
                    component={RouterLink}
                    to={`/asset-overview?tab=artisans&search=${task.artisan.id}`}
                    onClick={(e) => e.stopPropagation()}
                  >
                    {task.artisan.company_name}
                  </Link>
                </Box>
              )}
            </Box>

            <Box mb={2}>
              <Typography variant="body2" fontWeight="bold">
                Beschreibung:
              </Typography>
              <Typography variant="body2">{task.description}</Typography>
            </Box>

            {task.attachments && task.attachments.length > 0 && (
              <Box mb={2}>
                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                  Anhänge:
                </Typography>
                <Grid container spacing={1}>
                  {task.attachments.map((att) => (
                    <Grid item xs={12} sm={6} md={4} key={att.id}>
                      <Paper
                        elevation={0}
                        sx={{
                          p: 1,
                          bgcolor: "#f5f5f5",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          border: "1px solid #eee",
                        }}
                      >
                        <Box
                          display="flex"
                          alignItems="center"
                          gap={1}
                          overflow="hidden"
                        >
                          <InsertDriveFileIcon color="action" />
                          <Box overflow="hidden">
                            <Typography
                              variant="body2"
                              noWrap
                              title={att.filename}
                            >
                              {att.filename}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {new Date(att.created_at).toLocaleDateString()}
                            </Typography>
                          </Box>
                        </Box>
                        <Box display="flex">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDownloadAttachment(task.id, att);
                            }}
                            title="Herunterladen"
                          >
                            <DownloadIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteAttachment(task.id, att.id);
                            }}
                            color="error"
                            title="Löschen"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}

            {task.emails && task.emails.length > 0 && (
              <Box mb={2}>
                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                  Verknüpfte E-Mails:
                </Typography>
                <Grid container spacing={1}>
                  {task.emails.map((email) => (
                    <Grid item xs={12} sm={12} md={6} key={email.id}>
                      <Paper
                        elevation={0}
                        sx={{
                          p: 1,
                          bgcolor: "#e3f2fd", // Light blue for emails
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          border: "1px solid #bbdefb",
                          cursor: "pointer",
                          "&:hover": {
                            bgcolor: "#bbdefb",
                          },
                        }}
                        component={RouterLink}
                        to={`/emails?id=${email.id}`}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Box
                          display="flex"
                          alignItems="center"
                          gap={1}
                          overflow="hidden"
                          sx={{ width: "100%" }}
                        >
                          <EmailIcon color="primary" />
                          <Box overflow="hidden" sx={{ minWidth: 0, flex: 1 }}>
                            <Typography
                              variant="body2"
                              fontWeight="medium"
                              noWrap
                              title={email.subject}
                            >
                              {email.subject || "(Kein Betreff)"}
                            </Typography>
                            <Box display="flex" justifyContent="space-between">
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                noWrap
                              >
                                Von: {email.sender}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ ml: 1, flexShrink: 0 }}
                              >
                                {new Date(email.date).toLocaleDateString() +
                                  " " +
                                  new Date(email.date).toLocaleTimeString([], {
                                    hour: "2-digit",
                                    minute: "2-digit",
                                  })}
                              </Typography>
                            </Box>
                          </Box>
                        </Box>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}

            {subtasks[task.id] && subtasks[task.id].length > 0 && (
              <Box mt={2}>
                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                  Unteraufgaben:
                </Typography>
                <Grid container spacing={2}>
                  {["todo", "in_progress", "done"].map((status) => {
                    const statusSubtasks = subtasks[task.id].filter(
                      (st) => st.status === status,
                    );
                    if (statusSubtasks.length === 0) return null;

                    let statusLabel = "Todo";
                    let statusColor = "default";
                    if (status === "in_progress") {
                      statusLabel = "In Bearbeitung";
                      statusColor = "warning";
                    }
                    if (status === "done") {
                      statusLabel = "Erledigt";
                      statusColor = "success";
                    }

                    return (
                      <Grid item xs={12} md={4} key={status}>
                        <Box
                          sx={{
                            border: "1px solid #eee",
                            borderRadius: 1,
                            p: 1,
                            bgcolor: "white",
                          }}
                        >
                          <Typography
                            variant="caption"
                            fontWeight="bold"
                            color={statusColor + ".main"}
                          >
                            {statusLabel} ({statusSubtasks.length})
                          </Typography>
                          <Stack spacing={1} mt={1}>
                            {statusSubtasks.map((st) => (
                              <Paper
                                key={st.id}
                                elevation={0}
                                onClick={() => handleOpenDialog(st)}
                                sx={{
                                  p: 1,
                                  bgcolor: st.is_archived
                                    ? "rgba(255, 255, 0, 0.1)"
                                    : "#f5f5f5",
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                  cursor: "pointer",
                                  "&:hover": {
                                    bgcolor: st.is_archived
                                      ? "rgba(255, 255, 0, 0.2)"
                                      : "#e0e0e0",
                                  },
                                }}
                              >
                                <Box>
                                  <Box
                                    display="flex"
                                    alignItems="center"
                                    gap={1}
                                  >
                                    {st.is_archived && (
                                      <Chip
                                        label="Archiv"
                                        size="small"
                                        color="warning"
                                        variant="outlined"
                                        sx={{ height: 20, fontSize: "0.6rem" }}
                                      />
                                    )}
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        textDecoration:
                                          st.status === "done"
                                            ? "line-through"
                                            : "none",
                                      }}
                                    >
                                      {st.title}
                                    </Typography>
                                  </Box>
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    {getPriorityLabel(st.priority)}
                                  </Typography>
                                </Box>
                                {st.is_archived &&
                                  currentUser?.is_superuser && (
                                    <Box>
                                      <IconButton
                                        size="small"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleDeleteArchivedTask(
                                            st.id,
                                            task.id,
                                          );
                                        }}
                                        color="error"
                                        title="Dauerhaft löschen"
                                      >
                                        <DeleteForeverIcon fontSize="small" />
                                      </IconButton>
                                    </Box>
                                  )}
                              </Paper>
                            ))}
                          </Stack>
                        </Box>
                      </Grid>
                    );
                  })}
                </Grid>
              </Box>
            )}
          </TableCell>
        </TableRow>
      )}
    </React.Fragment>
  );

  return (
    <MDBox>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {successMessage}
        </Alert>
      )}

      <MDBox
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Box display="flex" alignItems="center" gap={2}>
          <MDTypography variant="h4" fontWeight="medium">
            Aufgaben
          </MDTypography>
          <Tooltip title="Aktualisieren">
            <IconButton
              onClick={() => fetchTasks(true)}
              disabled={loading}
              size="small"
            >
              <RefreshIcon
                sx={{ animation: loading ? "spin 1s linear infinite" : "none" }}
              />
            </IconButton>
          </Tooltip>
          <style>
            {`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}
          </style>
        </Box>

        <Box display="flex" gap={2} alignItems="center">
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(e, newView) => {
              if (newView) setViewMode(newView);
            }}
            aria-label="view mode"
            size="small"
            sx={{
              height: 42,
              bgcolor: "background.paper",
              border: "1px solid #e0e0e0",
            }}
          >
            <ToggleButton value="list" aria-label="list view">
              <ViewListIcon />
            </ToggleButton>
            <ToggleButton value="board" aria-label="board view">
              <ViewModuleIcon />
            </ToggleButton>
          </ToggleButtonGroup>

          {currentUser?.is_superuser && (
            <MDButton
              variant="outlined"
              color="error"
              size="medium"
              startIcon={
                isClearing ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  <DeleteForeverIcon />
                )
              }
              onClick={handleClearArchivedTasks}
              disabled={isClearing}
            >
              Archiv
            </MDButton>
          )}
          <MDButton
            variant="gradient"
            color="info"
            size="medium"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            Neue Aufgabe
          </MDButton>
        </Box>
      </MDBox>

      {viewMode === "list" ? (
        <TableContainer component={Paper}>
          <Table sx={{ minWidth: 650 }} aria-label="tasks table">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: "bold" }}>Titel</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Priorität</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Zugewiesen an</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Erstellt am</TableCell>
                <TableCell align="right" sx={{ fontWeight: "bold" }}>
                  Aktionen
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      gap={2}
                    >
                      <CircularProgress size={24} />
                      <Typography>Aufgaben werden geladen...</Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                <>
                  <TableRow>
                    <TableCell
                      colSpan={5}
                      sx={{
                        fontWeight: "bold",
                        backgroundColor: "rgba(244, 67, 54, 0.1)", // Error color light
                        color: "error.main",
                        textAlign: "left",
                        fontSize: "1rem",
                        py: 1.5,
                      }}
                    >
                      ToDo ({todoTasks.length})
                    </TableCell>
                  </TableRow>
                  {todoTasks.map(renderTaskRow)}
                  <TableRow>
                    <TableCell
                      colSpan={5}
                      sx={{
                        fontWeight: "bold",
                        backgroundColor: "rgba(255, 152, 0, 0.1)", // Warning color light
                        color: "warning.main",
                        textAlign: "left",
                        fontSize: "1rem",
                        py: 1.5,
                      }}
                    >
                      In Bearbeitung ({inProgressTasks.length}){" "}
                    </TableCell>
                  </TableRow>
                  {inProgressTasks.map(renderTaskRow)}
                  <TableRow>
                    <TableCell
                      colSpan={5}
                      sx={{
                        fontWeight: "bold",
                        backgroundColor: "rgba(76, 175, 80, 0.1)", // Success color light
                        color: "success.main",
                        textAlign: "left",
                        fontSize: "1rem",
                        py: 1.5,
                      }}
                    >
                      Erledigt ({doneTasks.length})
                    </TableCell>
                  </TableRow>
                  {doneTasks.map(renderTaskRow)}
                </>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Grid container spacing={3}>
          {renderBoardColumn("ToDo", todoTasks, "error")}
          {renderBoardColumn("In Bearbeitung", inProgressTasks, "warning")}
          {renderBoardColumn("Erledigt", doneTasks, "success")}
        </Grid>
      )}

      {/* Task Dialog */}
      <TaskDialog
        open={open}
        onClose={handleCloseDialog}
        task={editTask}
        parentId={selectedParentId}
        users={users}
        currentUser={currentUser}
        onSave={handleSaveTask}
        onDelete={(taskId, parentId) => {
          handleDeleteTask(taskId, parentId);
        }}
      />
    </MDBox>
  );
};

export default TodoList;
